import _extends from "@babel/runtime/helpers/esm/extends";
import debounce from 'lodash.debounce';
import isEqual from 'lodash.isequal';
import { globalObject } from '@skbkontur/global-object';
import { isNonNullable } from "../../../lib/utils";
import { isKeyArrowUp, isKeyArrowVertical, isKeyEnter, isKeyEscape } from "../../../lib/events/keyboard/identifiers";
import * as LayoutEvents from "../../../lib/LayoutEvents";
import { DefaultState } from "../CustomComboBox";
import { ComboBoxRequestStatus } from "../CustomComboBoxTypes";
var DEBOUNCE_DELAY = 300;

var getValueString = function getValueString(value, valueToString) {
  return isNonNullable(value) ? valueToString(value) : '';
};

export var Effect = {
  search: function search(query) {
    return function (dispatch, getState, getProps, getInstance) {
      getInstance().search(query);
    };
  },
  debouncedSearch: debounce(function (dispatch, getState, getProps, getInstance) {
    var searchEffect = Effect.search(getState().textValue);
    searchEffect(dispatch, getState, getProps, getInstance);
  }, DEBOUNCE_DELAY),
  cancelRequest: function cancelRequest(dispatch, getState, getProps, getInstance) {
    Effect.debouncedSearch.cancel();
    getInstance().cancelSearch();
  },
  blur: function blur(dispatch, getState, getProps) {
    var _getProps = getProps(),
        onBlur = _getProps.onBlur;

    if (onBlur) {
      onBlur();
    }
  },
  focus: function focus(dispatch, getState, getProps) {
    var _getProps2 = getProps(),
        onFocus = _getProps2.onFocus;

    if (onFocus) {
      onFocus();
    }
  },
  valueChange: function valueChange(value) {
    return function (dispatch, getState, getProps) {
      var _getProps3 = getProps(),
          onValueChange = _getProps3.onValueChange;

      if (onValueChange) {
        onValueChange(value);
      }
    };
  },
  unexpectedInput: function unexpectedInput(textValue, items) {
    return function (dispatch, getState, getProps) {
      var _getProps4 = getProps(),
          onUnexpectedInput = _getProps4.onUnexpectedInput,
          valueToString = _getProps4.valueToString;

      if (Array.isArray(items) && items.length === 1) {
        var singleItem = items[0];
        var valueContent = getValueString(singleItem, valueToString);

        if (valueContent === textValue) {
          dispatch({
            type: 'ValueChange',
            value: singleItem,
            keepFocus: false
          });
          return;
        }
      }

      if (onUnexpectedInput) {
        var _value = onUnexpectedInput(textValue);

        if (_value !== undefined) {
          dispatch({
            type: 'ValueChange',
            value: _value,
            keepFocus: false
          });
        }
      }
    };
  },
  inputChange: function inputChange(dispatch, getState, getProps) {
    var _getProps5 = getProps(),
        onInputValueChange = _getProps5.onInputValueChange;

    var _getState = getState(),
        textValue = _getState.textValue;

    if (onInputValueChange) {
      var returnedValue = onInputValueChange(textValue);

      if (typeof returnedValue === 'string' && returnedValue !== textValue) {
        dispatch({
          type: 'TextChange',
          value: returnedValue
        });
      }
    }
  },
  inputFocus: function inputFocus(dispatch, getState, getProps, getInstance) {
    var _getInstance = getInstance(),
        input = _getInstance.input;

    if (!input) {
      return;
    }

    input.focus();
  },
  highlightMenuItem: function highlightMenuItem(dispatch, getState, getProps, getInstance) {
    var _getProps6 = getProps(),
        value = _getProps6.value,
        itemToValue = _getProps6.itemToValue,
        valueToString = _getProps6.valueToString;

    var _getState2 = getState(),
        items = _getState2.items,
        focused = _getState2.focused,
        textValue = _getState2.textValue,
        requestStatus = _getState2.requestStatus;

    var _getInstance2 = getInstance(),
        menu = _getInstance2.menu;

    var valueString = getValueString(value, valueToString);

    if (!menu) {
      return;
    }

    if (!focused) {
      return;
    }

    var index = -1;

    if (items && items.length && isNonNullable(value)) {
      index = items.findIndex(function (x) {
        return itemToValue(x) === itemToValue(value);
      });
    }

    menu.highlightItem(index);

    if (index >= 0) {
      // @ts-expect-error: Use of private property.
      globalObject.requestAnimationFrame == null ? void 0 : globalObject.requestAnimationFrame(function () {
        return menu && menu.scrollToSelected();
      });
      return;
    }

    if (textValue !== valueString || requestStatus === ComboBoxRequestStatus.Failed) {
      globalObject.requestAnimationFrame == null ? void 0 : globalObject.requestAnimationFrame(function () {
        return menu && menu.down();
      });
    }
  },
  selectMenuItem: function selectMenuItem(event) {
    return function (dispatch, getState, getProps, getInstance) {
      var _getInstance3 = getInstance(),
          menu = _getInstance3.menu;

      if (menu) {
        menu.enter(event);
      }
    };
  },
  moveMenuHighlight: function moveMenuHighlight(direction) {
    return function (dispatch, getState, getProps, getInstance) {
      var _getInstance4 = getInstance(),
          menu = _getInstance4.menu;

      if (menu) {
        menu[direction]();
      }
    };
  },
  resetHighlightedMenuItem: function resetHighlightedMenuItem(dispatch, getState, getProps, getInstance) {
    var combobox = getInstance();

    if (combobox.menu && combobox.menu.hasHighlightedItem()) {
      combobox.menu.reset();
    }
  },
  reflow: function reflow() {
    LayoutEvents.emit();
  },
  selectInputText: function selectInputText(dispatch, getState, getProps, getInstance) {
    var combobox = getInstance();
    combobox.selectInputText();
  },
  inputKeyDown: function inputKeyDown(event) {
    return function (dispatch, getState, getProps) {
      var _getProps7 = getProps(),
          onInputKeyDown = _getProps7.onInputKeyDown;

      if (onInputKeyDown) {
        onInputKeyDown(event);
      }
    };
  }
};

var never = function never() {
  return null;
};

export function reducer(state, props, action) {
  switch (action.type) {
    case 'ValueChange':
      {
        var _value2 = action.value,
            keepFocus = action.keepFocus;

        var _textValue = getValueString(_value2, props.valueToString);

        if (keepFocus) {
          return [{
            opened: false,
            inputChanged: false,
            editing: true,
            items: null,
            textValue: _textValue
          }, [Effect.valueChange(_value2), Effect.cancelRequest, Effect.inputFocus]];
        }

        return [{
          opened: false,
          inputChanged: false,
          editing: false,
          items: null,
          textValue: _textValue
        }, [Effect.valueChange(_value2), Effect.cancelRequest]];
      }

    case 'TextChange':
      {
        var newState = {
          inputChanged: true,
          textValue: action.value
        };

        if (!action.value && !props.searchOnFocus) {
          return [_extends({}, newState, {
            opened: false,
            items: null
          }), [Effect.inputChange]];
        }

        return [newState, [Effect.debouncedSearch, Effect.inputChange]];
      }

    case 'KeyPress':
      {
        var e = action.event;
        var effects = [];
        var nextState = state;

        switch (true) {
          case isKeyEnter(e):
            if (!state.opened) {
              break;
            }

            e.preventDefault();
            effects.push(Effect.selectMenuItem(e));
            break;

          case isKeyArrowVertical(e):
            e.preventDefault();
            effects.push(Effect.moveMenuHighlight(isKeyArrowUp(e) ? 'up' : 'down'));

            if (!state.opened) {
              effects.push(Effect.search(state.textValue));
            }

            break;

          case isKeyEscape(e):
            nextState = _extends({}, state, {
              items: null,
              opened: false
            });
            break;
        }

        return [nextState, [].concat(effects, [Effect.inputKeyDown(e)])];
      }

    case 'DidUpdate':
      {
        if (isEqual(props.value, action.prevProps.value)) {
          return state;
        }

        if (action.fixValueChange) {
          var nextTextValue = getValueString(props.value, props.valueToString);

          if (!state.focused) {
            return [{
              editing: false,
              inputChanged: false,
              textValue: nextTextValue
            }, [Effect.cancelRequest]];
          }

          if (state.focused && state.opened) {
            return [_extends({}, state, {
              textValue: nextTextValue
            }), [Effect.cancelRequest, Effect.search(nextTextValue)]];
          }

          if (state.focused) {
            return _extends({}, state, {
              textValue: nextTextValue
            });
          }
        }

        return {
          opened: false,
          textValue: state.editing ? state.textValue : getValueString(props.value, props.valueToString)
        };
      }

    case 'Mount':
      {
        return {
          textValue: getValueString(props.value, props.valueToString)
        };
      }

    case 'Focus':
      {
        var _newState = {
          focused: true,
          editing: true
        };

        if (!props.searchOnFocus) {
          return [_newState, [Effect.focus]];
        }

        if (state.editing) {
          return [_newState, [Effect.search(state.textValue), Effect.focus]];
        }

        return [_newState, [Effect.search(''), Effect.focus, Effect.selectInputText]];
      }

    case 'InputClick':
      {
        var _newState2 = {
          inputChanged: false
        };

        if (!state.opened && props.searchOnFocus) {
          return [_newState2, [Effect.search('')]];
        }

        return state;
      }

    case 'Blur':
      {
        var inputChanged = state.inputChanged,
            _items = state.items;

        if (!inputChanged) {
          return [{
            focused: false,
            opened: false,
            items: null,
            editing: false
          }, [Effect.blur, Effect.cancelRequest]];
        }

        return [{
          focused: false,
          opened: false,
          items: null
        }, [Effect.blur, Effect.cancelRequest, Effect.unexpectedInput(state.textValue, _items)]];
      }

    case 'Reset':
      {
        return DefaultState;
      }

    case 'Open':
      {
        return {
          opened: true
        };
      }

    case 'Close':
      {
        return {
          opened: false,
          items: null
        };
      }

    case 'Search':
      {
        return [state, [Effect.search(action.query)]];
      }

    case 'RequestItems':
      {
        return {
          loading: true,
          opened: true,
          requestStatus: ComboBoxRequestStatus.Pending
        };
      }

    case 'ReceiveItems':
      {
        var shouldResetMenuHighlight = state.textValue === '';
        return [{
          loading: false,
          opened: true,
          items: action.items,
          requestStatus: ComboBoxRequestStatus.Success
        }, [shouldResetMenuHighlight ? Effect.resetHighlightedMenuItem : Effect.highlightMenuItem, Effect.reflow]];
      }

    case 'RequestFailure':
      {
        return [{
          loading: false,
          opened: true,
          items: null,
          requestStatus: ComboBoxRequestStatus.Failed,
          repeatRequest: action.repeatRequest
        }, [Effect.highlightMenuItem]];
      }

    case 'CancelRequest':
      {
        return {
          loading: false,
          requestStatus: ComboBoxRequestStatus.Unknown
        };
      }

    default:
      {
        never();
      }
  }

  return state;
}